import { PropTypes, React } from 'imports';
import { FormattedResource, MarkdownBlock, Tooltip } from '..';
import { invalidMessagePropType } from '../EntityEditForm/EntityEditForm';
import Popup from '../Popup/Popup';
import { Listbox, WysiwygEditor } from 'typed/components/common';
import { deepEqual, isAncestorOrSelf } from 'utils';
import { injectIntl, intlShape } from 'react-intl';
import './TextInput.less';

const IS_IE = (() => !/*@cc_on!@*/true)() || !!document.documentMode || window.navigator.userAgent.toLowerCase().includes('edge');
const IS_FF = navigator.userAgent.toLowerCase().includes('firefox');

const
    MENTION_TRIGGER = '@',
    MENTION_WORD_SEPARATORS = '.,;:!?*#+()@[]'; // regex testing does not work in FF

class TextInput extends React.Component {
    static get propTypes() {
        return {
            intl: intlShape.isRequired,

            value: PropTypes.string,
            placeholder: FormattedResource.resourcePropType,
            icon: PropTypes.string,
            debounce: PropTypes.number,
            inline: PropTypes.bool,
            multiline: PropTypes.bool,
            autofocus: PropTypes.bool,
            invalid: invalidMessagePropType,
            disabled: PropTypes.bool,
            clearable: PropTypes.bool,
            markdownHint: PropTypes.bool,
            markdownWysiwyg: PropTypes.bool,
            hideWysiwygToolbar: PropTypes.bool,
            mentionSuggestions: PropTypes.arrayOf(PropTypes.object),
            className: PropTypes.string,
            maxLength: PropTypes.number,
            onFocus: PropTypes.func,
            onChange: PropTypes.func,
            onPasteImage: PropTypes.func,
            onPasteText: PropTypes.func,
            onMentioned: PropTypes.func,
            TeamMentioned: PropTypes.func,
            onSubmit: PropTypes.func,
            onCancel: PropTypes.func,
            onBlur: PropTypes.func
        };
    }

    static get defaultProps() {
        return {
            value: '',
            placeholder: '',
            debounce: 300,
            inline: false,
            multiline: false,
            autofocus: false,
            invalid: false,
            disabled: false,
            clearable: true,
            markdownHint: false,
            markdownWysiwyg: false,
            hideWysiwygToolbar: false,
            className: ''
        };
    }

    state = {
        mentionPopupData: null
    };

    _mentioningTriggered = false;

    _debounceTimeoutID = null;

    _cachedInnerText = '';

    _getInnerText() {
        return this._cachedInnerText = this.refs.input.innerText
            .replace(/\u00A0/g, ' ') // browsers intersperse spaces with nbsp in long spaces sequences to prevent space collapsing
            .replace(!IS_IE && !IS_FF ? /\n$/g : '', ''); // https://stackoverflow.com/questions/27786048/why-is-my-contenteditable-cursor-jumping-to-the-end-in-chrome
    }

    _setInnerText(value) {
        if (this._cachedInnerText === value) {
            return;
        }
        if (!this.props.multiline) {
            value = value.replace(/\n/g, ' ');
        }
        // inversion of _getValue
        this.refs.input.innerText = this._cachedInnerText = value
            .replace(/  /g, ' \u00A0') +
            (!IS_IE && !IS_FF ? '\n' : '');

        if (window.getSelection && isAncestorOrSelf(this.refs.input, window.getSelection().focusNode)) {
            this._fixSelection(false);
        }
    }

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentDidMount() {
        this.componentDidUpdate();

        if (this.props.autofocus) {
            this.refs.input.focus();

            this._fixSelection(false);
        }
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.value !== nextProps.value) {
            if (this._debounceTimeoutID) {
                clearTimeout(this._debounceTimeoutID);
                this._debounceTimeoutID = null;
            }
        }
    }

    shouldComponentUpdate(nextProps) {
        return (
            this.props.intl.locale !== nextProps.intl.locale ||
            this.props.value !== nextProps.value ||
            !FormattedResource.equal(this.props.placeholder, nextProps.placeholder) ||
            this.props.icon !== nextProps.icon ||
            this.props.inline !== nextProps.inline ||
            this.props.multiline !== nextProps.multiline ||
            (
                typeof nextProps.invalid === 'boolean' ?
                    this.props.invalid !== nextProps.invalid :
                    !FormattedResource.equal(this.props.invalid, nextProps.invalid)
            ) ||
            this.props.disabled !== nextProps.disabled ||
            this.props.clearable !== nextProps.clearable ||
            this.props.className !== nextProps.className ||
            this.props.markdownHint !== nextProps.markdownHint ||
            this.props.markdownWysiwyg !== nextProps.markdownWysiwyg ||
            !deepEqual(this.props.mentionSuggestions, nextProps.mentionSuggestions)
        );
    }

    componentDidUpdate() {
        // dangerouslySetInnerHTML has bug https://github.com/facebook/react/issues/377 causing innerHTML not to update when changed
        // also probably for the same reason innerHTML sometimes gets reassigned even if not changed, which causes cursor to lose it's position
        // so we don't use dangerouslySetInnerHTML but instead set innerHTML manually, only when it's actually changed
        // it would be nice also to preserve cursor position, but it's not so important:
        // when user types something, innerHTML already has latest value and innerHTML won't be reassigned,
        // and if changes come from outside, most likely field has no focus
        this._setInnerText(this.props.value);
    }

    componentWillUnmount() {
        if (this._debounceTimeoutID) {
            this._onInputDebounced();
        }
    }

    _fixSelection(selectAll) {
        if (document.selection) {
            const range = document.body.createTextRange();
            range.moveToElementText(this.refs.input);
            if (!selectAll) {
                range.collapse(false);
            }
            range.select();
        }
        else if (window.getSelection) {
            const range = document.createRange();
            range.selectNodeContents(this.refs.input);
            if (!selectAll) {
                range.collapse(false);
            }
            window.getSelection().removeAllRanges();
            window.getSelection().addRange(range);
        }
    }

    _isMentionSupported() {
        return !!this.props.mentionSuggestions && !this.props.markdownWysiwyg;
    }

    _isMentioning() {
        return this._isMentionSupported() && !!this.state.mentionPopupData?.initialized;
    }

    _getFilteredMentionSuggestions() {
        const
            { mentionSuggestions } = this.props,
            { mentionPopupData } = this.state,

            suggestionQueryString = mentionPopupData?.initialized ?
                mentionPopupData.filter.toLowerCase() :
                null;

        if (!mentionSuggestions) {
            return [];
        }

        return mentionSuggestions.filter(({ FilterDisplayName }) => !suggestionQueryString || FilterDisplayName.toLowerCase().includes(suggestionQueryString));
    }

    _getCursorRect() {
        const isSupported = typeof window.getSelection !== 'undefined';

        if (!isSupported) {
            return null;
        }

        const selection = window.getSelection();

        if (selection.rangeCount === 0) {
            return null;
        }

        const range = selection.getRangeAt(0).cloneRange();
        range.collapse(true);

        return range ? range.getClientRects()[0] : null;
    }

    _getCursorPosition() {
        const isSupported = typeof window.getSelection !== 'undefined';

        if (!isSupported) {
            return 0;
        }

        const selection = window.getSelection();

        if (selection.rangeCount) {
            const range = selection.getRangeAt(0);
            return range.endOffset;
        }

        return 0;
    }

    _setCursorAtPosition(cursorOffset) {
        const isSupported = typeof window.getSelection !== 'undefined';

        if (!isSupported) {
            return 0;
        }

        const
            input = this.refs.input,
            range = document.createRange(),
            selection = window.getSelection();

        range.selectNodeContents(input);
        range.setStart(input.firstChild, cursorOffset);
        range.collapse(true);

        selection.removeAllRanges();
        selection.addRange(range);
    }

    _isCursorOutsideMentioning = () => {
        if (!this._isMentioning()) {
            return true;
        }

        const
            mentionPopupData = this.state.mentionPopupData,
            { filterStartCursorLeftOffset, filterStartCursorRightOffset } = mentionPopupData,
            text = this._getInnerText() || '',
            position = this._getCursorPosition();

        return position <= filterStartCursorLeftOffset || position > text.length - filterStartCursorRightOffset;
    };

    _getMentionTypedWord = () => {
        const
            mentionPopupData = this.state.mentionPopupData,
            { filterStartCursorLeftOffset, filterStartCursorRightOffset } = mentionPopupData,
            text = this._getInnerText() || '',
            textTail = text.slice(filterStartCursorLeftOffset);

        return textTail.slice(0, textTail.length - filterStartCursorRightOffset);
    };

    _initMentionSuggestionsPopup = () => {
        const
            isMentionSupported = this._isMentionSupported(),
            cursorRect = this._getCursorRect();

        if (!cursorRect || !isMentionSupported) {
            this.setState({ mentionPopupData: null });
            return;
        }

        const
            windowScrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop,
            popupPadding = 5,
            docElement = document.scrollingElement || document.documentElement,
            visibleDocHeight = docElement.clientHeight,
            showOnTop = cursorRect.bottom > visibleDocHeight * 2 / 3;

        let
            popupTop = null,
            popupBottom = null,
            popupContentMaxHeight = null;

        if (showOnTop) {
            popupBottom = windowScrollTop + cursorRect.top - popupPadding;
            popupContentMaxHeight = cursorRect.top - cursorRect.height - popupPadding * 3;
        }
        else {
            popupTop = windowScrollTop + cursorRect.bottom + popupPadding;
            popupContentMaxHeight = visibleDocHeight - cursorRect.bottom - cursorRect.height - popupPadding * 3;
        }

        const
            text = this._getInnerText() || '',
            cursorPosition = this._getCursorPosition();

        this.setState({
            mentionPopupData: {
                initialized: true,
                top: popupTop,
                bottom: popupBottom,
                left: cursorRect.left,
                popupContentMaxHeight,
                filter: '',
                filterStartCursorLeftOffset: cursorPosition - 1,
                filterStartCursorRightOffset: text.length - cursorPosition,
                activeSuggestionIndex: 0
            }
        });
    };

    _updateMentionFilter = () => {
        if (!this._isMentioning()) {
            return;
        }

        const
            { mentionPopupData } = this.state,
            typedWord = this._getMentionTypedWord();

        this.setState({
            mentionPopupData: {
                ...mentionPopupData,
                filter: typedWord.startsWith(MENTION_TRIGGER) && typedWord.length > 1 ? typedWord.slice(1).toLowerCase() : '',
                activeSuggestionIndex: 0
            }
        });
    };

    _stopMentioning = (forceUpdate = false) => {
        this.setState({
            mentionPopupData: null
        }, () => {
            if (forceUpdate) {
                this.forceUpdate();
            }
        });
    };

    _replaceMentionText = (entityType, entityID) => {
        if (!this._isMentioning()) {
            this._stopMentioning(true);
            return;
        }

        const
            mentionPopupData = this.state.mentionPopupData,
            { filterStartCursorLeftOffset, filterStartCursorRightOffset } = mentionPopupData,
            text = this._getInnerText() || '',

            { mentionSuggestions } = this.props,
            suggestions = mentionSuggestions || [],
            mentionedEntity = suggestions.find(({ ID, EntityType }) => EntityType === entityType && ID === +entityID),
            mentionNewText = mentionedEntity.EditorDisplayName + ' ',

            newText = [
                text.slice(0, filterStartCursorLeftOffset),
                mentionNewText,
                text.slice(text.length - filterStartCursorRightOffset)
            ].join('');

        this._setInnerText(newText);
        this._setCursorAtPosition(newText.length - filterStartCursorRightOffset);

        this._debounceTimeoutID = setTimeout(this._onInputDebounced, this.props.debounce);
        this._stopMentioning();

        if (this.props.onMentioned && mentionedEntity) {
            this.props.onMentioned(this, mentionedEntity);
        }
    };

    _onMouseDown = () => {
        if (this.props.disabled) {
            return;
        }

        this._mouseDowned = true;

        if (IS_IE) {
            this.refs.input.focus();
        }
    };

    _onFocus = (event) => {
        if (this.props.disabled) {
            return;
        }

        const
            isArtificialFocusOnEmptyField = !this.props.value && !event.isTrusted && event.relatedTarget,
            fixSelection = (this.props.value && event.relatedTarget) || isArtificialFocusOnEmptyField,
            relatedTargetIsChildElement = event.relatedTarget && this.refs.root.contains(event.relatedTarget);

        if (fixSelection && !relatedTargetIsChildElement) {
            this._fixSelection(!this._mouseDowned);
        }

        this._mouseDowned = false;

        if (this.props.onFocus) {
            this.props.onFocus(this, event);
        }
    };

    _onMouseUp = () => {
        this._mouseDowned = false;

        const
            { mentionPopupData } = this.state,
            isMentionSupported = this.props.mentionSuggestions && !this.props.markdownWysiwyg,
            isMentioning = isMentionSupported && mentionPopupData?.initialized;

        if (isMentioning && this._isCursorOutsideMentioning()) {
            this._stopMentioning(true);
        }
    };

    _onInputDebounced = (cleanup) => {
        if (this._debounceTimeoutID) {
            clearTimeout(this._debounceTimeoutID);
            this._debounceTimeoutID = null;
        }

        if (this.props.disabled) {
            return;
        }

        const dirtyValue = this._getInnerText();

        let cleanValue = cleanup ?
            dirtyValue
                .replace(/^\s+/g, '') // remove leading spaces/tabs/newlines in the beginning
                .replace(/\s+$/g, '') // and trailing ones
                .replace(/\r/g, '') // use unix line ending
                .replace(/[^\S\n]/g, ' ') // replace all space-like characters (tab, nbsp) with normal spaces
            :
            dirtyValue;

        if (this.props.maxLength !== undefined && cleanValue.length > this.props.maxLength) {
            cleanValue = cleanValue.substr(0, this.props.maxLength);
        }

        if (dirtyValue !== cleanValue) {
            this._setInnerText(cleanValue);
        }

        const
            isMentionSupported = this._isMentionSupported(),
            isMentioning = this._isMentioning();

        if (isMentioning && !this._mentioningTriggered) {
            if (this._isCursorOutsideMentioning()) {
                this._stopMentioning();
            }
            else {
                this._updateMentionFilter();
            }
        }

        if (this.props.value !== cleanValue && isMentionSupported && this._mentioningTriggered) {
            this._initMentionSuggestionsPopup();
            this._mentioningTriggered = false;
        }

        if (this.props.onChange && this.props.value !== cleanValue) {
            this.props.onChange(this, cleanValue);
        }
    };

    _onInput = () => {
        if (this._debounceTimeoutID) {
            clearTimeout(this._debounceTimeoutID);
            this._debounceTimeoutID = null;
        }

        if (this.props.disabled) {
            return;
        }


        const value = this._getInnerText();

        if (
            !this.props.debounce ||
            !this.props.value && value ||
            this.props.value && !value
        ) {
            this._onInputDebounced();
        }
        else {
            this._debounceTimeoutID = setTimeout(this._onInputDebounced, this.props.debounce);
        }
    };

    _onKeyDown = (event) => {
        const
            { mentionPopupData } = this.state,
            isMentionSupported = this._isMentionSupported(),
            isMentioning = this._isMentioning(),
            mentionSuggestions = isMentioning ? this._getFilteredMentionSuggestions() : [],
            currentChar = event.key && event.key.length === 1 ? event.key : '';

        if (this.props.disabled) {
            return;
        }

        if (event.keyCode === 13 && (event.shiftKey || !this.props.multiline) && !isMentioning) { // enter
            event.preventDefault();

            this._onInputDebounced(true);

            if (this.props.onSubmit) {
                this.props.onSubmit(this, this._getInnerText());
            }
        }

        if ((event.keyCode === 13 || event.keyCode === 9) && isMentioning) { // enter or tab
            event.preventDefault();

            const
                selectedSuggestionIndex = mentionPopupData.activeSuggestionIndex,
                entity = mentionSuggestions[selectedSuggestionIndex];

            this._replaceMentionText(entity.EntityType, entity.ID);
        }

        if (event.keyCode === 27) { // escape
            if (isMentioning) {
                this._stopMentioning(true);
                return;
            }

            if (this.props.onCancel) {
                this.props.onCancel(this);
            }
        }

        if (currentChar === MENTION_TRIGGER && isMentionSupported) { // '@' sign
            const
                text = this._getInnerText() || '',
                cursorPosition = this._getCursorPosition(),
                previousLetter = cursorPosition > 0 && text.length > 0 ? text[cursorPosition - 1] : '',
                startMentioning = previousLetter.length === 0 || previousLetter === ' ' || MENTION_WORD_SEPARATORS.includes(previousLetter);

            if (startMentioning) {
                this._mentioningTriggered = true;
            }
        }

        if (MENTION_WORD_SEPARATORS.includes(currentChar) && isMentioning) {
            this._stopMentioning();
        }

        if (event.keyCode === 40 && isMentioning) { // down
            event.preventDefault();

            const newIndex = mentionPopupData.activeSuggestionIndex + 1;

            this.setState({
                mentionPopupData: {
                    ...mentionPopupData,
                    activeSuggestionIndex: newIndex < mentionSuggestions.length ? newIndex : mentionSuggestions.length - 1
                }
            }, () => {
                this.forceUpdate();
            });
        }

        if (event.keyCode === 38 && isMentioning) { // up
            event.preventDefault();

            const newIndex = mentionPopupData.activeSuggestionIndex - 1;

            this.setState({
                mentionPopupData: {
                    ...mentionPopupData,
                    activeSuggestionIndex: newIndex > 0 ? newIndex : 0
                }
            }, () => {
                this.forceUpdate();
            });
        }

        let cursorOutsideMentioning = false;

        if (event.keyCode === 37 && isMentioning) { // left
            const
                prevPosition = this._getCursorPosition(),
                newPosition = prevPosition > 0 ? prevPosition - 1 : 0;

            cursorOutsideMentioning = newPosition <= mentionPopupData.filterStartCursorLeftOffset;
        }

        if (event.keyCode === 39 && isMentioning) { // right
            const
                prevPosition = this._getCursorPosition(),
                text = this._getInnerText() || '',
                newPosition = prevPosition < text.length ? prevPosition + 1 : text.length;

            cursorOutsideMentioning = newPosition <= mentionPopupData.filterStartCursorLeftOffset ||
                newPosition > text.length - mentionPopupData.filterStartCursorRightOffset;
        }

        if (cursorOutsideMentioning) {
            this._stopMentioning(true);
        }

        if (IS_IE && (event.keyCode === 46 || event.keyCode === 8) && !isMentioning) {
            // for some reason in some cases when all the text in a contentEditable node is selected
            // and user hits Del or Backspace, Edge might remove the whole node
            // which is totally an Edge bug, but I couldn't find any place to submit a report (simple example - https://jsfiddle.net/fk9pdmo0/2/)
            // looks like the problem happens when selection touches the edge of the node
            // so let's detect that case and don't let Edge remove the content, but do it ourselves
            // which might be not the correct behavior if selection starts/ends in the middle of the text,
            // but implementing a custom editing would be quite problematic
            // still better than not be able to type text at all
            const selection = window.getSelection();
            if ((
                selection.anchorNode === this.refs['input'] ||
                selection.focusNode === this.refs['input'] ||
                selection.anchorOffset === 0 ||
                selection.focusOffset === 0 ||
                selection.anchorOffset === this._getInnerText().length ||
                selection.focusOffset === this._getInnerText().length
            ) && !selection.isCollapsed) {
                this._onClearClick(event);
                event.preventDefault();
            }
        }
    };

    _onPasteImage = (file, event) => {
        let fileExtension = "";
        const dotIndex = file.name.lastIndexOf(".");

        if (dotIndex >= 0) {
            fileExtension = file.name.substring(dotIndex);
        }

        const mimeType = file.type;
        const dataURL = event.target.result;

        if (this.props.onPasteImage) {
            this.props.onPasteImage(this, fileExtension, mimeType, dataURL);
        }

        const { input } = this.refs;
        if (input) {
            const children = input.children;

            for (let childIndex = children.length - 1; childIndex >= 0; childIndex--) {
                const child = children[childIndex];

                if (child.tagName === "IMG") {
                    input.removeChild(child);
                }
            }
        }
    }

    _onPaste = (event) => {
        const matchRule = /image.*/;
        let imageFound = false;

        Array.prototype.forEach.call(event.clipboardData.types, (type, i) => {
            if (!imageFound && (type.match(matchRule) || event.clipboardData.items[i].type.match(matchRule))) {
                const file = event.clipboardData.items[i].getAsFile();

                let reader = new FileReader();
                reader.onload = this._onPasteImage.bind(this, file);
                reader.readAsDataURL(file);

                imageFound = true;
            }
        });

        if (imageFound) return;

        if (IS_IE) {
            const text = event.clipboardData.getData("text");
            const selection = window.getSelection ? window.getSelection() : null;

            if (selection && selection.getRangeAt && selection.rangeCount) {
                let range = selection.getRangeAt(0);
                range.deleteContents();
                range.insertNode(document.createTextNode(text));
            } else if (document.selection && document.selection.createRange) {
                document.selection.createRange().text = text;
            }
        } else {
            document.execCommand("insertText", false, event.clipboardData.getData("text/plain"));
        }

        event.preventDefault();
        this._onInputDebounced(true);
    }

    _onClearClick = (event) => {
        if (this.props.disabled) {
            return;
        }

        this._setInnerText('');
        this._onInputDebounced();

        event.stopPropagation();

        this.refs.input.focus();
        this._fixSelection(false);
    }

    _onBlur = (event) => {
        if (this.props.disabled) {
            return;
        }

        if (isAncestorOrSelf(this.refs.root, event.relatedTarget)) {
            this.refs.input.focus();
            return;
        }

        this._onInputDebounced(true);

        if (this.props.onBlur) {
            this.props.onBlur(this, this._getInnerText(), event);
        }
    }

    _onSuggestionChange = (_sender, selectOptionID) => {
        const [entityType, entityID] = selectOptionID.split('-');
        this._replaceMentionText(entityType, entityID);
    };

    _onSuggestionsMouseEnter = () => {
        const { mentionPopupData } = this.state;

        if (mentionPopupData) {
            this.setState({
                mentionPopupData: {
                    ...mentionPopupData,
                    activeSuggestionIndex: -1
                }
            }, () => {
                this.forceUpdate();
            });
        }
    };

    _onSuggestionsMouseLeave = () => {
        const { mentionPopupData } = this.state;

        if (mentionPopupData) {
            this.setState({
                mentionPopupData: {
                    ...mentionPopupData,
                    activeSuggestionIndex: 0
                }
            }, () => {
                this.forceUpdate();
            });
        }
    };

    _onSuggestionsMouseDown = (event) => {
        // prevent loosing editor area focus
        event.preventDefault();
        event.stopPropagation();
    };

    _renderMentionSuggestionsPopup = () => {
        const { mentionPopupData } = this.state;

        if (!mentionPopupData || !mentionPopupData.initialized || this.props.markdownWysiwyg) {
            return null;
        }

        const mentionSuggestions = this._getFilteredMentionSuggestions();

        return (
            <Popup
                top={mentionPopupData.top}
                bottom={mentionPopupData.bottom}
                left={mentionPopupData.left}
                fixed={false}
                className="mention-suggestions-modal"
                closeMode={Popup.CloseMode.OUTSIDE}
            >
                {
                    mentionSuggestions.length ?
                        <div
                            className="suggestions-wrapper"
                            style={{ maxHeight: `${mentionPopupData.popupContentMaxHeight}px` }}
                            onMouseEnter={this._onSuggestionsMouseEnter}
                            onMouseLeave={this._onSuggestionsMouseLeave}
                            onMouseDown={this._onSuggestionsMouseDown}
                        >
                            <Listbox onChange={this._onSuggestionChange}>
                                {
                                    mentionSuggestions
                                        .map((suggestion, index) =>
                                            <Listbox.Option
                                                key={`${suggestion.EntityType}-${suggestion.ID}`}
                                                value={`${suggestion.EntityType}-${suggestion.ID}`}
                                                className={index === mentionPopupData.activeSuggestionIndex ? 'active' : ''}
                                            >
                                                {suggestion.FilterDisplayName}
                                            </Listbox.Option>
                                        )
                                }
                            </Listbox>
                        </div> :
                        <div className="suggestions-wrapper">
                            <FormattedResource className="empty" resource={{ id: 'common.nothing_to_show' }} />
                        </div>
                }
            </Popup>
        );
    };

    render() {
        const markdownHint = this.props.markdownHint && !this.props.markdownWysiwyg;

        return (
            <div ref="root" className={'text-input ' +
                (this.props.className || '') +
                (this.props.value ? '' : ' empty') +
                (this.props.inline ? ' inline' : '') +
                (this.props.multiline ? ' multiline' : ' singleline') +
                (markdownHint ? ' with-markdown-hint' : '') +
                (this.props.markdownWysiwyg ? ' with-markdown-wysiwyg' : '') +
                (!this.props.multiline && this.props.clearable ? ' clearable' : '') +
                (this.props.invalid ? ' invalid' : '') +
                (this.props.disabled ? ' disabled' : '')}
            >

                {markdownHint ? MarkdownBlock.MarkdownBlockHint : null}

                {this.props.icon ? <div className={`fa fa-${this.props.icon}`} /> : null}

                {
                    this.props.markdownWysiwyg && !this.props.disabled ?
                        <WysiwygEditor
                            value={this.props.value}
                            autofocus={this.props.autofocus}
                            placeholder={this.props.placeholder}
                            hideToolbar={this.props.hideWysiwygToolbar}
                            inline={this.props.inline}
                            mentionSuggestions={this.props.mentionSuggestions}
                            onChange={this.props.onChange}
                            onBlur={this.props.onBlur}
                            onPasteImage={this.props.onPasteImage}
                            onPasteText={this.props.onPasteText}
                            onMentioned={this.props.onMentioned}
                            onSubmit={this.props.onSubmit}
                        /> :
                        null
                }

                {/* always render placeholder to use it's width as a min-width to avoid jumps when user starts typing*/}
                <div className="placeholder" onMouseDown={this._onMouseDown} onMouseUp={this._onMouseUp}>
                    <FormattedResource resource={this.props.placeholder} />
                </div>
                <div ref="input" className="input" contentEditable={!this.props.disabled} suppressContentEditableWarning onMouseDown={this._onMouseDown} onMouseUp={this._onMouseUp}
                    onFocus={this._onFocus} onInput={this._onInput} onPaste={this._onPaste} onBlur={this._onBlur} onKeyDown={this._onKeyDown} onKeyUp={IS_IE ? this._onInput : null}>
                    {!IS_IE && !IS_FF ? <br /> : null /*magic, check _getInnerText*/}
                </div>
                {
                    !this.props.multiline && !this.props.inline && !this.props.disabled && this.props.value && this.props.clearable ?
                        <button className="inline fa fa-times" tabIndex="-1" onClick={this._onClearClick} /> :
                        null
                }
                {this.props.children}
                {
                    this.props.invalid && this.props.invalid !== true ?
                        <Tooltip triggerType={[Tooltip.TriggerType.HOVER, Tooltip.TriggerType.FOCUS]}>
                            <FormattedResource resource={this.props.invalid} />
                        </Tooltip> :
                        null
                }
                {this._renderMentionSuggestionsPopup()}
            </div>
        );
    }
}

export default injectIntl(TextInput);
